import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Clock, FileDigit, MapPin, School, ShieldCheck } from 'lucide-react';

const SEARCH_OPTIONS = [
  { id: 1, title: 'A burial or cremation site', icon: School, link: '/cemeteries' },
  { id: 4, title: 'Cemetery planning & digitalization assistance', icon: FileDigit, link: '/contact' },
  { id: 5, title: 'Looking to become a preferred vendor', icon: Building, link: '/contact' },
];

const TRUST = [
  { icon: MapPin, label: 'Cemeteries mapped online' },
  { icon: ShieldCheck, label: 'Dignified, transparent care' },
  { icon: Clock, label: 'Available 24 / 7' },
];

const Home = () => (
  <div className="bg-slate-50">
    {/* Hero */}
    <div className="relative min-h-[620px]">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/home/hero-image.jpg')" }}
      />
      {/* A navy-tinted graded scrim: it keeps the headline legible and gives the
          photograph a subtle brand cast rather than a neutral grey wash. */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-950/82 to-navy-950/92" />

      <div className="container relative mx-auto flex min-h-[620px] flex-col items-center justify-center px-4 pb-28 pt-16 text-center">
        <p className="eyebrow animate-fade-up text-navy-100">Compassionate cemetery care</p>
        <h1 className="mt-5 max-w-4xl animate-fade-up text-4xl leading-[1.1] text-white md:text-6xl">
          Welcome to Cemetery &amp; Burial Solutions
        </h1>
        <p className="mt-6 max-w-xl animate-fade-up font-montserrat text-base leading-relaxed text-slate-200 md:text-lg">
          Creating meaningful memorials and providing comprehensive cemetery services with
          dignity and respect.
        </p>
        <div className="mt-10 flex animate-fade-up flex-wrap justify-center gap-3">
          <Link to="/cemeteries" className="btn-on-dark">
            Find a cemetery
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="btn-outline-light">
            Speak with us
          </Link>
        </div>

        <div className="mt-12 flex animate-fade-up flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {TRUST.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="flex items-center gap-2 font-montserrat text-xs tracking-wide text-slate-100"
            >
              <Icon className="h-4 w-4 text-navy-200" />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Search options — lifted onto the hero as a single card. */}
    <div className="relative z-10 -mt-16 pb-20">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-8 shadow-card-hover">
          <div className="mb-6 text-center">
            <p className="eyebrow">How can we help</p>
            <h2 className="mt-2 text-3xl">Are you searching for&hellip;</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {SEARCH_OPTIONS.map((option) => (
              <Link
                key={option.id}
                to={option.link}
                className="group flex h-full flex-col items-center justify-start gap-4 rounded-xl border border-slate-200 bg-slate-50/70 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:bg-white hover:shadow-card"
              >
                <span className="icon-chip transition-transform duration-300 group-hover:scale-110 group-hover:bg-navy-100">
                  <option.icon className="h-6 w-6" />
                </span>
                <span className="font-montserrat text-sm font-medium leading-snug text-slate-700 group-hover:text-navy-900">
                  {option.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Burial and Cremation Services */}
    <section className="bg-white py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className="eyebrow">For families</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Burial and Cremation Services</h2>
          <div className="rule mt-5" />
          <p className="mt-5 font-montserrat leading-relaxed text-slate-600">
            We provide comprehensive burial &amp; cremation services that combine dignity with
            flexibility, allowing families to create meaningful farewells that honor their loved
            ones&rsquo; wishes.
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 md:grid-cols-2">
          {[
            {
              src: '/images/home/cremation-costs.jpg',
              alt: 'A family reviewing cremation and burial costs with an advisor',
              title: 'Understanding Costs',
              body: 'We believe in transparent pricing and helping families understand all available options. Whether it is in a columbarium or an in-ground burial, our sites are affordably priced to meet your financial considerations.',
            },
            {
              src: '/images/home/provider-choice.jpg',
              alt: 'An urn resting among flowers at a memorial service',
              title: 'Choosing the Right Provider',
              body: 'Making an informed decision about cremation and burial services is crucial. We guide you through important considerations including service options, facilities, and professional credentials.',
            },
          ].map((card) => (
            <article key={card.title} className="group flex flex-col">
              <div className="overflow-hidden rounded-2xl shadow-card">
                <img
                  src={card.src}
                  alt={card.alt}
                  className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <h3 className="mt-6 text-2xl">{card.title}</h3>
              <p className="mt-3 font-montserrat text-sm leading-relaxed text-slate-600">
                {card.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* Planning Ahead */}
    <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 items-stretch gap-12 md:grid-cols-2 lg:gap-16">
          <div className="overflow-hidden rounded-2xl shadow-card">
            <img
              src="/images/home/planning-ahead.jpg"
              alt="An older couple sitting together on a park bench in autumn"
              className="h-full min-h-[420px] w-full object-cover"
            />
          </div>
          <div>
            <p className="eyebrow">Peace of mind</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Planning Ahead</h2>
            <p className="mt-5 font-montserrat leading-relaxed text-slate-600">
              Planning ahead is a meaningful act of love. It brings peace of mind and eases the
              emotional and financial strain your family may face during an already difficult
              time. Thoughtful preparation includes several important elements: financial
              planning, personal wishes, and family support. C&amp;BS can provide you with a
              planning guide to start gathering the information.
            </p>

            <div className="mt-8 space-y-4">
              {[
                {
                  title: 'Wills and Trusts',
                  body: 'Establishing a will or trust is an essential part of planning ahead. Whether you plan to leave money or personal possessions, making your wishes known is an important step to ensure those wishes are honored.',
                },
                {
                  title: 'Personal Wishes',
                  body: 'Document your preferences for your service, memorialization, personal effects, and other important decisions to provide clarity and guidance for your loved ones.',
                },
                {
                  title: 'Family Support',
                  body: 'Access resources and guidance to help facilitate important conversations with family members about your plans and wishes.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="group rounded-xl border border-slate-200 bg-white p-6 shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:border-navy-200 hover:shadow-card-hover"
                >
                  <h3 className="flex items-center gap-2 text-xl">
                    <span className="h-4 w-1 rounded-full bg-navy-700 transition-all duration-300 group-hover:h-5" />
                    {item.title}
                  </h3>
                  <p className="mt-2 font-montserrat text-sm leading-relaxed text-slate-600">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Cemetery & Cremation Property */}
    <section className="bg-white py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className="eyebrow">For cemeteries</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Cemetery &amp; Cremation Property</h2>
          <div className="rule mt-5" />
          <p className="mt-5 font-montserrat leading-relaxed text-slate-600">
            Whether you&rsquo;re a small or large organization, we can help enhance your cemetery
            with a variety of options — from designing columbaria to making your cemetery records
            digital.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {[
            {
              src: '/images/home/cemetery-types.jpg',
              alt: 'Headstones bordered by flowering plantings',
              eyebrow: 'Records',
              title: 'Digital Record Keeping',
              body: 'Create a digital copy of your cemetery records and a digital layout of your cemetery with our tracking system. No matter the state of your records, we can help.',
            },
            {
              src: '/images/home/cemetery-costs.jpg',
              alt: 'A jar of savings set aside for funeral costs',
              eyebrow: 'Planning',
              title: 'Cemetery Investment',
              body: 'Understand the various factors that influence property costs, and learn how to optimize your cemetery for burials and spacing.',
            },
            {
              src: '/images/home/cremation-gardens.jpg',
              alt: 'A memorial bench in a landscaped cremation garden',
              eyebrow: 'Cremation',
              title: 'Cremation Memorials',
              body: 'Discover cremation gardens and columbaria designs that offer serene spaces for reflection and remembrance, with options for both single and family placements.',
            },
          ].map((card) => (
            <article
              key={card.title}
              className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
            >
              <div className="overflow-hidden">
                <img
                  src={card.src}
                  alt={card.alt}
                  className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="eyebrow text-[10px] text-navy-600">{card.eyebrow}</span>
                <h3 className="mt-2 text-xl">{card.title}</h3>
                <p className="mt-3 font-montserrat text-sm leading-relaxed text-slate-600">
                  {card.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* Closing call to action */}
    <section className="relative overflow-hidden bg-navy-gradient py-24 text-center text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(50% 60% at 50% 0%, rgba(78,143,233,0.35) 0%, rgba(8,45,104,0) 70%)',
        }}
      />
      <div className="container relative mx-auto px-4">
        <p className="eyebrow text-navy-200">Reserve online</p>
        <h2 className="mt-3 text-3xl text-white md:text-4xl">Ready to find a resting place?</h2>
        <p className="mx-auto mt-4 max-w-xl font-montserrat leading-relaxed text-navy-100">
          Browse the cemeteries we manage, view an interactive site map, and reserve a site
          online — or speak with our team about pre-planning.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/cemeteries" className="btn-on-dark">
            Find a cemetery
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="btn-outline-light">
            Contact us
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
